<template>
    <div class="rounded border border-slate-200 bg-white">
        <div class="flex flex-wrap items-center gap-1.5 p-2">
            <span class="rounded bg-slate-100 px-1.5 py-0.5 font-mono font-medium text-slate-700">{{ log.model || "(unknown model)" }}</span>
            <span v-if="log.call_type" class="text-slate-400">{{ log.call_type }}</span>
            <span class="text-slate-500" title="input / output / cache / reasoning">
                {{ fmt(log.input_tokens) }}in / {{ fmt(log.output_tokens) }}out
                <template v-if="log.cache_matched_input_tokens"> / {{ fmt(log.cache_matched_input_tokens) }}cache</template>
                <template v-if="log.reasoning_tokens"> / {{ fmt(log.reasoning_tokens) }}think</template>
            </span>
            <span v-if="log.duration_ms != null" class="text-slate-500">{{ fmt(log.duration_ms) }}ms</span>
            <span v-if="log.exception" class="rounded-full bg-red-100 px-1.5 py-0.5 font-medium text-red-700">error</span>
            <span v-else-if="log.http_status" class="text-slate-400">HTTP {{ log.http_status }}</span>
            <button
                type="button"
                class="ml-auto rounded border border-slate-300 px-1.5 py-0.5 text-slate-600 hover:bg-slate-50"
                data-testid="call-log-raw-toggle"
                @click="emit('toggle-raw')">
                {{ raw?.open ? "Hide raw" : "Raw" }}
            </button>
        </div>

        <div v-if="log.exception" class="border-t border-slate-100 px-2 py-1 text-red-700 whitespace-pre-wrap">{{ log.exception }}</div>

        <div v-if="raw?.open" class="border-t border-slate-100 p-2">
            <div v-if="raw.loading" class="text-slate-400">Loading…</div>
            <template v-else-if="raw.data">
                <template v-if="raw.data.messages_json">
                    <div class="mb-1 text-[10px] font-medium uppercase text-slate-400">Request messages</div>
                    <pre class="max-h-72 overflow-auto rounded bg-slate-50 p-2 font-mono text-[11px] text-slate-700 whitespace-pre-wrap">{{ raw.data.messages_json }}</pre>
                </template>
                <template v-if="raw.data.response_json">
                    <div class="mb-1 mt-2 text-[10px] font-medium uppercase text-slate-400">Response</div>
                    <pre class="max-h-72 overflow-auto rounded bg-slate-50 p-2 font-mono text-[11px] text-slate-700 whitespace-pre-wrap">{{ raw.data.response_json }}</pre>
                </template>
                <div v-if="!raw.data.messages_json && !raw.data.response_json" class="text-slate-400">No request/response payload stored for this call.</div>
                <div class="mt-2 font-mono text-[10px] text-slate-400">
                    provider_request_id: {{ raw.data.provider_request_id || "—" }} · trace_id: {{ raw.data.trace_id || "—" }}
                </div>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { CallLogRaw, CallLogSummary } from "./types";

defineProps<{
    log: CallLogSummary;
    raw?: { open: boolean; loading: boolean; data: CallLogRaw | null };
}>();
const emit = defineEmits<{ "toggle-raw": [] }>();

const fmt = (n: number): string => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));
</script>
